import React, { useEffect, useState, useMemo, useRef } from 'react';
import { MRT_ColumnDef } from 'material-react-table';
import api from 'api';
import { Tooltip, CircularProgress, makeStyles, Fab } from '@material-ui/core';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import Grid from '@material-ui/core/Grid';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import moment from 'moment';
import {
  getFundColumns,
  getModelPortfolioColumns,
  resetTableState
} from 'components/InvestmentAnalyser/utils';
import PersistentTable from 'components/InvestmentAnalyser/PersistentTable';
import { Button, Prompt } from 'components/Base';
import FilterOption from './FilterOption';
import FilterOptionWrapper from './FilterOptionWrapper';
import FilterOptionRange from './FilterOptionRange';
import FilterOptionDate from './FilterOptionDate';
import { chargeTypeEnum } from 'components/CipManagementv2/components/CipThreshold';
import FilterOptionNestedRange from './FilterOptionNestedRange';
import { ArrowLeftIcon, ArrowRightIcon } from '@mui/x-date-pickers';
import Icons from 'components/Icons';
import SaveAdvancedFilterModal from './SaveAdvancedFilterModal';
import ExistingAdvancedFiltersModal from './ExistingAdvancedFiltersModal';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useCSVDownloader } from 'react-papaparse';
import { Notify } from 'utils';
import { GlobalStyles } from '@mui/material';
import PortfolioAllocationsModal from 'components/InvestmentAnalyser/PortfolioAllocationsModal';
import { useHistory } from 'react-router-dom';
import { compose } from 'redux-starter-kit';
import { connect } from 'react-redux';
import Store from 'store/store';
import { isEqual } from 'lodash';
import { useTheme } from '@material-ui/styles';

const { selectors } = Store;

const useStyles = makeStyles({
  page: {
    height: '100%',
    width: '100%',
    padding: '42px',
    backgroundColor: '#fafafa',
    position: 'fixed',
    top: 0,
    left: 0,
    zIndex: 10,
    overflow: 'scroll'
  },
  loaderWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%'
  },
  link: {
    float: 'right',
    fontWeight: 600,
    color: '#4469a6',
    fontSize: '0.9rem',
    cursor: 'pointer',
    marginRight: '0.6rem'
  },
  label: {
    color: 'rgba(0, 0, 0, 0.54)',
    fontWeight: 500,
    fontSize: '1.1rem',
    transform: 'translate(0, 1.5px) scale(0.75)',
    transformOrigin: 'top left'
  }
});

interface AdvancedFiltersProps {
  open: boolean;
  addInvestments: Function;
  type: 'fund' | 'portfolio';
  close: Function;
  riskProfiler: string;
}

const AdvancedFilters = ({
  open,
  type,
  close,
  addInvestments,
  riskProfiler
}: AdvancedFiltersProps) => {
  const theme = useTheme() as any;
  const history = useHistory();
  const classes = useStyles();
  const fundsTableId = `advanced_filter_${type}`;

  const [isLoading, setIsLoading] = useState(true);
  const [rawInvestments, setRawInvestments] = useState([] as any);
  const investments = useMemo(() => rawInvestments, [rawInvestments]);
  const [options, setOptions] = useState([] as any);
  const [filters, setFilters] = useState({} as any);
  const [count, setCount] = useState(0);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [totalNumberOfInvestments, setTotalNumberOfInvestments] =
    useState<number>(0);
  const [riskRatings, setRiskRatings] = useState({} as any);
  const [selectedRows, setSelectedRows] = useState({} as any);
  const [filterConfig, setFilterConfig] = useState([] as any);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(true);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showExistingModal, setShowExistingModal] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedMpName, setSelectedMpName] = useState('');
  const [funds, setFunds] = useState([]);
  const [fetchingPortfolio, setFetchingPortfolio] = useState(false);
  const [targetLocation, setTargetLocation] = useState(null) as any;
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [isModalVisible, setModalVisible] = useState(false);
  const [dynamicColumns, setDynamicColumns] = useState({} as any);
  const [sorting, setSorting] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [activePayload, setActivePayload] = useState({} as any);

  const unblock = useRef<any>();

  const proceed = () => {
    if (!targetLocation) {
      return;
    }

    if (unblock.current) {
      unblock.current();
    }

    history.push(targetLocation);
  };

  useEffect(() => {
    if (!open) {
      if (unblock.current) {
        unblock.current(); // Unblock navigation when closing
      }
      return;
    }

    if (confirmLeave) {
      proceed();
      return;
    }

    // Block navigation only when open is true
    unblock.current = history.block((location: any) => {
      setTargetLocation(location);
      setModalVisible(true);
      return false;
    });

    return () => {
      if (unblock.current) {
        unblock.current(); // Cleanup to prevent memory leaks
      }
    };
  }, [open, history, confirmLeave]);

  const cleanFilters = (filters: { [x: string]: any }) => {
    const cleanedFilters = Object.keys(filters).reduce((acc: any, key: any) => {
      let value = filters[key];

      if (
        [
          'equity_style',
          'fixed_income_style',
          // 'modelportfolioriskscore_set__time_horizon',
          'modelportfolioriskscore_set__risk_level'
        ].includes(key)
      ) {
        value = value.map((val: any) => {
          return options[key].find((option: any) => option.name === val)?.value;
        });
      }

      if (value && (!Array.isArray(value) || value.length > 0)) {
        if (
          [
            'cumulative',
            'annualised',
            'calendar',
            'max_drawdown',
            'alpha',
            'beta',
            'information_ratio',
            'r_squared',
            'sharpe_ratio',
            'standard_deviation'
          ].includes(key)
        ) {
          acc = { ...acc, ...value };
        } else {
          acc[key] = value;
        }
      }
      return acc;
    }, {});

    if (globalFilter) {
      cleanedFilters['search'] = globalFilter;
    }

    return cleanedFilters;
  };

  const staticFundColumns = getFundColumns();

  let investmentColumns = [] as any;
  if (type === 'fund') {
    investmentColumns = getFundColumns({
      riskProfiler,
      includeAllocation: false,
      includeExpanded: true,
      options
    });
  } else {
    investmentColumns = getModelPortfolioColumns({
      riskProfiler,
      includeAllocation: false,
      riskRatings,
      forceType: true,
      includeExpanded: true,
      theme,
      classes
    });
  }

  const columns = useMemo<MRT_ColumnDef<any>[]>(
    () => investmentColumns as any,
    [investments, isLoading]
  );

  const fundColumns = useMemo<MRT_ColumnDef<any>[]>(
    () => staticFundColumns as any,
    [funds, isLoading]
  );

  const initialColumnVisibility = columns.reduce((acc: any, row: any) => {
    acc[row.accessorKey] = row.visible;
    return acc;
  });

  const [loadingOptions, setLoadingOptions] = useState(true);

  useEffect(() => {
    if (!open) return;

    setGlobalFilter('');

    setLoadingOptions(true);
    const request =
      type === 'fund'
        ? api.getFundOptions()
        : api.getModelPortfolioSelectionOptions();

    request
      .then(({ data }) => {
        setOptions(data);
        setLoadingOptions(false);
      })
      .catch(() => {
        setLoadingOptions(false);
      });
  }, [open]);

  const fetchInvestments = (params = {}) => {
    const newPayload = { filters: cleanFilters(filters), params, sorting };

    if (isEqual(activePayload, newPayload)) {
      setIsLoading(false);
      return;
    }

    setIsTableLoading(true);
    setActivePayload(newPayload);

    const request =
      type === 'fund'
        ? api.filterFunds(newPayload)
        : api.filterModelPortfolioSelections(newPayload);

    request
      .then(response => {
        setRawInvestments(response.data.results);
        setTotalRows(response.data.count || 0);
        setTotalNumberOfInvestments(response.data.total);
        setRiskRatings(response.data.risk_ratings);
        if (isLoading) {
          setIsLoading(false);
        }
      })
      .catch(() => {
        setIsLoading(false);
      })
      .finally(() => {
        setCount(count + 1);
        setIsTableLoading(false);
      });
  };

  const isRangeObject = (value: any) => {
    const expectedKeys = ['min', 'max'];
    return (
      value &&
      typeof value === 'object' &&
      Object.keys(value).length === expectedKeys.length &&
      expectedKeys.every(key => key in value)
    );
  };

  useEffect(() => {
    if (open) {
      fetchInvestments();
    } else {
      setRawInvestments([]);
    }
  }, [open]);

  useEffect(() => {
    fetchInvestments(activePayload?.params || {});
  }, [filters, sorting, globalFilter]);

  useEffect(() => {
    setFilterConfig([
      {
        label: `${type === 'fund' ? 'Fund' : 'Portfolio'} characteristics`,
        open: true,
        filters: [
          {
            component: FilterOption,
            label: 'Provider',
            field: type === 'fund' ? 'group_name' : 'firm_name',
            dataKey: type === 'fund' ? 'group_name' : 'firm_name'
          },
          {
            component: FilterOption,
            label: 'Investment type',
            field: 'investment_type',
            dataKey: 'investment_type',
            isVisible: type === 'fund'
          },
          {
            component: FilterOption,
            label: 'IA sector',
            field: 'ia_sector',
            dataKey: 'ia_sector',
            isVisible: type === 'fund'
          },
          {
            component: FilterOption,
            label: 'Morningstar Sector',
            field: 'sector_name',
            dataKey: 'sector_name'
          },
          {
            component: FilterOptionRange,
            label: 'Annual yield (%)',
            field: 'annual_yield',
            dataKey: 'annual_yield',
            isVisible: type === 'fund'
          },
          {
            component: FilterOptionDate,
            label: 'Inception Date',
            field: 'inception_date'
          },
          {
            component: FilterOptionRange,
            label: 'Manager tenure (years)',
            field: 'managers__tenure',
            dataKey: 'managers__tenure',
            props: {
              type: null
            },
            isVisible: type === 'fund'
          },
          {
            component: FilterOptionRange,
            label: 'Fund size (£)',
            field: 'size',
            dataKey: 'size',
            props: {
              type: chargeTypeEnum.MONETARY
            },
            isVisible: type === 'fund'
          },
          {
            component: FilterOption,
            label: 'Leveraged fund',
            field: 'leveraged',
            dataKey: 'leveraged',
            isVisible: type === 'fund'
          },
          {
            component: FilterOption,
            label: 'Distribution status',
            field: 'distribution_status',
            dataKey: 'distribution_status',
            isVisible: type === 'fund'
          },
          {
            component: FilterOption,
            label: 'Currency',
            field: 'base_currency',
            dataKey: 'base_currency',
            isVisible: type === 'fund'
          },
          {
            component: FilterOption,
            label: 'Morningstar equity style',
            field: 'equity_style',
            dataKey: 'equity_style',
            isVisible: type === 'fund',
            props: {
              options: (options.equity_style || []).map(
                ({ name, value }: any) => ({
                  name,
                  value
                })
              )
            }
          },
          {
            component: FilterOption,
            label: 'Morningstar fixed income style',
            field: 'fixed_income_style',
            dataKey: 'fixed_income_style',
            isVisible: type === 'fund',
            props: {
              options: (options.fixed_income_style || []).map(
                ({ name, value }: any) => ({
                  name,
                  value
                })
              )
            }
          }
        ]
      },
      {
        label: 'Charges',
        filters: [
          {
            component: FilterOptionRange,
            label: 'Initial Fee (%)',
            field: 'initial_charge'
          },
          {
            component: FilterOptionRange,
            label: 'OCF (%)',
            field: 'ocf'
          },
          {
            component: FilterOptionRange,
            label: 'Transaction costs (%)',
            field: 'trading_costs'
          }
        ]
      },
      {
        label: 'Performance',
        filters: [
          {
            component: FilterOptionNestedRange,
            label: 'Cumulative',
            field: 'cumulative',
            props: {
              options: {
                '3M': 'three_m_performance',
                '6M': 'six_m_performance',
                '1Y': 'one_y_performace',
                '3Y': 'three_y_performace',
                '5Y': 'five_y_performace'
              }
            }
          },
          {
            component: FilterOptionNestedRange,
            label: 'Annualised',
            field: 'annualised',
            props: {
              options: {
                '1Y': 'one_y_performace',
                '3Y': 'annualised_three_y_performance',
                '5Y': 'annualised_five_y_performance'
              }
            }
          },
          {
            component: FilterOptionNestedRange,
            label: 'Calendar',
            field: 'calendar',
            props: {
              options: (() => {
                const years = Array.from({ length: 5 }, (_, i) =>
                  moment()
                    .subtract(i + 1, 'years')
                    .year()
                    .toString()
                );

                const numberStrings = ['one', 'two', 'three', 'four', 'five'];

                return years.reduce((obj, year, index) => {
                  (obj as any)[year] =
                    `calendar_${numberStrings[index]}_y_performance`;
                  return obj;
                }, {});
              })()
            }
          }
        ]
      },
      {
        label: 'Risk',
        filters: [
          {
            component: FilterOptionNestedRange,
            label: 'Max drawdown',
            field: 'max_drawdown',
            props: {
              options: {
                '1Y': 'maximum_one_y_drawdown',
                '3Y': 'maximum_three_y_drawdown',
                '5Y': 'maximum_five_y_drawdown'
              }
            }
          },
          //Removed the lower the limit of calls to EV endpoint
          // {
          //   component: FilterOption,
          //   label: 'Investment time horizon (years)',
          //   field: 'modelportfolioriskscore_set__time_horizon',
          //   dataKey: 'modelportfolioriskscore_set__time_horizon'
          // },
          ...(riskProfiler === 'EValue'
            ? [
                {
                  component: FilterOption,
                  label: 'Risk Level',
                  field: 'modelportfolioriskscore_set__risk_level',
                  dataKey: 'modelportfolioriskscore_set__risk_level'
                }
              ]
            : [
                {
                  component: FilterOptionRange,
                  label: 'Growth assets',
                  field: 'modelportfoliocomfortranges__growth_asset_percentage',
                  dataKey:
                    'modelportfoliocomfortranges__growth_asset_percentage'
                },
                {
                  component: FilterOptionRange,
                  label: 'Risk comfort range',
                  field: 'modelportfoliocomfortranges__ok_risk',
                  dataKey: 'modelportfoliocomfortranges__ok_risk'
                }
              ])
        ]
      },
      {
        label: 'Breakdown',
        filters: [
          {
            component: FilterOptionNestedRange,
            label: 'Asset class',
            field: 'asset_class',
            props: {
              options: (() => {
                return (options.asset_class || []).reduce(
                  (acc: any, { name, value }: any) => {
                    acc[name] = value;
                    return acc;
                  },
                  {}
                );
              })()
            }
          },
          {
            component: FilterOptionNestedRange,
            label: 'Sector',
            field: 'sector',
            props: {
              options: (() => {
                return (options.sector || []).reduce(
                  (acc: any, { name, value }: any) => {
                    acc[name] = value;
                    return acc;
                  },
                  {}
                );
              })()
            }
          },
          {
            component: FilterOptionNestedRange,
            label: 'Country',
            field: 'country',
            props: {
              options: (() => {
                return (options.country || []).reduce(
                  (acc: any, { name, equity, fixed_income }: any) => {
                    acc[`${name} - Equity`] = equity;
                    acc[`${name} - Fixed income`] = fixed_income;
                    return acc;
                  },
                  {}
                );
              })()
            }
          },
          {
            component: FilterOptionNestedRange,
            label: 'Regional',
            field: 'regional',
            props: {
              options: (() => {
                return (options.regional || []).reduce(
                  (acc: any, { name, equity, fixed_income }: any) => {
                    acc[`${name} - Equity`] = equity;
                    acc[`${name} - Fixed income`] = fixed_income;
                    return acc;
                  },
                  {}
                );
              })()
            }
          }
        ]
      },
      {
        label: 'Ratios',
        filters: [
          {
            component: FilterOptionNestedRange,
            label: 'Alpha',
            field: 'alpha',
            props: {
              options: {
                '1Y': 'one_y_alpha',
                '3Y': 'three_y_alpha',
                '5Y': 'five_y_alpha'
              }
            }
          },
          {
            component: FilterOptionNestedRange,
            label: 'Beta',
            field: 'beta',
            props: {
              options: {
                '1Y': 'one_y_beta',
                '3Y': 'three_y_beta',
                '5Y': 'five_y_beta'
              }
            }
          },
          {
            component: FilterOptionNestedRange,
            label: 'Information ratio',
            field: 'information_ratio',
            props: {
              options: {
                '1Y': 'one_y_information_ratio',
                '3Y': 'three_y_information_ratio',
                '5Y': 'five_y_information_ratio'
              }
            }
          },
          {
            component: FilterOptionNestedRange,
            label: 'R-squared',
            field: 'r_squared',
            props: {
              options: {
                '1Y': 'one_y_r_squared',
                '3Y': 'three_y_r_squared',
                '5Y': 'five_y_r_squared'
              }
            }
          },
          {
            component: FilterOptionNestedRange,
            label: 'Sharpe ratio',
            field: 'sharpe_ratio',
            props: {
              options: {
                '1Y': 'one_y_sharpe_ratio',
                '3Y': 'three_y_sharpe_ratio',
                '5Y': 'five_y_sharpe_ratio'
              }
            }
          },
          {
            component: FilterOptionNestedRange,
            label: 'Volatility',
            field: 'standard_deviation',
            props: {
              options: {
                '1Y': 'one_y_standard_deviation',
                '3Y': 'three_y_standard_deviation',
                '5Y': 'five_y_standard_deviation'
              }
            }
          }
        ]
      }
    ]);
  }, [type, options]);

  const reset = (key: string) => {
    resetTableState(key);
    setGlobalFilter('');
    setCount(count + 1);
  };

  const { CSVDownloader } = useCSVDownloader();

  const isFilterFilled = (value: any, key: string) => {
    if (typeof value === 'object') {
      if (Array.isArray(value)) {
        return value.length > 0;
      } else {
        const rangeObject = isRangeObject(value) ? value : value[key];

        if (!rangeObject) {
          return false;
        }

        return rangeObject.min !== null || rangeObject.max !== null;
      }
    }

    return false;
  };

  const handleFilterChange = (filterName: string, selectedValue: string) => {
    setFilters((prevFilters: any) => {
      const currentValue = prevFilters[filterName];
      // Ensure that undefined is used for empty values
      const newValue = selectedValue || undefined;
      if (currentValue !== newValue) {
        return { ...prevFilters, [filterName]: newValue };
      }
      return prevFilters;
    });

    const dynamicColumnNames =
      typeof selectedValue === 'object' &&
      !Array.isArray(selectedValue) &&
      !isRangeObject(selectedValue)
        ? Object.values(
            filterConfig
              .flatMap((item: any) => item.filters)
              .find((filter: any) => filter.field === filterName)?.props
              ?.options || {}
          ) || [filterName]
        : [filterName];

    dynamicColumnNames.forEach((dynamicColumnName: string) => {
      setDynamicColumns((prevValues: any) => {
        return {
          ...prevValues,
          [dynamicColumnName]: isFilterFilled(selectedValue, dynamicColumnName)
        };
      });
    });
  };

  const getCommonFilterProps = (filter: any) => {
    return {
      component: filter.component,
      key: filter.field,
      label: filter.label,
      field: filter.field,
      dataKey: filter.dataKey,
      selectedValue: filters[filter.field],
      onChange: handleFilterChange,
      isVisible: 'isVisible' in filter ? filter.isVisible : true,
      ...(filter?.props || {})
    };
  };

  const renderFilterComponent = (filter: any) => {
    const FilterComponent = filter.component;
    delete filter.component;

    return (
      <div>
        <div className={classes.label}>{filter.label}</div>
        <FilterComponent
          {...filter}
          data={filter.dataKey ? options[filter.dataKey] : undefined}
        />
      </div>
    );
  };

  const handleRowClick = (params: any) => {
    if (params.column.id === 'type' || params.column.id === 'name') {
      setFetchingPortfolio(true);
      setModalOpen(true);
      if (params.row && params.row.original.name) {
        setSelectedMpName(params.row.original.name);
      }

      api
        .getModelPortfolioSelectionAllocations({ id: params.row.original.id })
        .then((response: any) => {
          setFunds(response.data.fund_allocations);
        })
        .finally(() => {
          setFetchingPortfolio(false);
        });
    }
  };

  return open ? (
    <>
      <GlobalStyles
        styles={{
          body: {
            overflow: 'hidden'
          },
          '.MuiAppBar-root': {
            position: 'fixed'
          }
        }}
      />
      <div className={classes.page}>
        {isLoading || loadingOptions ? (
          <div className={classes.loaderWrapper}>
            <CircularProgress size="80px" />
          </div>
        ) : (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div style={{ paddingTop: '2rem', position: 'relative' }}>
              <div
                style={{
                  cursor: 'pointer',
                  position: 'absolute',
                  right: '1rem',
                  top: '3rem'
                }}
              >
                <Icons.CloseIcon height={24} onClick={() => close()} />
              </div>
              <Grid container spacing={2}>
                <Grid
                  item
                  xs={showAdvancedFilters ? 3 : 1}
                  style={{
                    ...(showAdvancedFilters ? {} : { maxWidth: 'max-content' })
                  }}
                >
                  {showAdvancedFilters && (
                    <div
                      style={{
                        display: 'flex',
                        gap: '1rem',
                        marginTop: '1rem',
                        marginBottom: '1rem'
                      }}
                    >
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => setShowExistingModal(true)}
                      >
                        Existing Filters
                      </Button>
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => setShowSaveModal(true)}
                      >
                        Save Filters
                      </Button>
                    </div>
                  )}
                  <div
                    style={{
                      paddingRight: '1rem',
                      position: 'relative'
                    }}
                  >
                    <Tooltip title={'Toggle menu'} arrow>
                      <Fab
                        onClick={() =>
                          setShowAdvancedFilters(!showAdvancedFilters)
                        }
                        color="secondary"
                        size="small"
                        aria-label="add"
                        style={{
                          position: 'absolute',
                          right: '0',
                          top: '0',
                          zIndex: '999'
                        }}
                      >
                        {showAdvancedFilters ? (
                          <ArrowLeftIcon />
                        ) : (
                          <ArrowRightIcon />
                        )}
                      </Fab>
                    </Tooltip>
                    <div
                      style={{
                        display: 'flex',
                        gap: '1rem',
                        flexDirection: 'column',
                        height: '100vh'
                        // overflowX: 'auto' // revert back if they want the sidebar to scroll
                      }}
                    >
                      {showAdvancedFilters && (
                        <div
                          className="dashboardLink"
                          style={{
                            marginTop: '1rem'
                          }}
                          onClick={() => {
                            setFilters({ firm_name: [], sector_name: [] });
                          }}
                        >
                          Reset Filters
                        </div>
                      )}
                      {showAdvancedFilters &&
                        filterConfig.map((section: any, index: number) => (
                          <FilterOptionWrapper
                            key={index}
                            label={section.label}
                            open={section.open}
                          >
                            {section.filters
                              .map((filter: any) =>
                                getCommonFilterProps(filter)
                              )
                              .filter((filter: any) => filter.isVisible)
                              .map((filter: any) =>
                                renderFilterComponent(filter)
                              )}
                          </FilterOptionWrapper>
                        ))}
                      <div style={{ padding: '1rem' }} />
                    </div>
                  </div>
                </Grid>

                <Grid
                  item
                  xs={showAdvancedFilters ? 9 : 11}
                  style={!showAdvancedFilters ? { maxWidth: '97%' } : {}}
                >
                  <div style={{ width: '100%', marginBottom: '2rem' }}>
                    <h2>
                      <span style={{ textTransform: 'capitalize' }}>
                        {type}
                      </span>{' '}
                      Filter
                    </h2>
                    <div
                      style={{ marginBottom: '1rem', marginTop: '-0.75rem' }}
                    >
                      Use this tool to search and filter our entire {type}{' '}
                      universe based on specific criteria. Apply filters across
                      key data columns to create a tailored shortlist. You can
                      sort each column and add or remove data columns as needed.
                      Once filtered, you can export data for up to{' '}
                      <span style={{ fontWeight: 600 }}>100 {type}s</span> or
                      add your shortlist directly into Investment Selection for
                      deeper analysis and comparison.
                    </div>
                    <h2>
                      <span style={{ textTransform: 'capitalize' }}>
                        {type}
                      </span>{' '}
                      shortlist: {totalRows.toLocaleString()} matches from{' '}
                      {type === 'fund' ? '' : 'MPS '}
                      directory of {totalNumberOfInvestments.toLocaleString()}{' '}
                      {type}s
                      <span
                        className={classes.link}
                        onClick={() => reset(`${fundsTableId}`)}
                      >
                        Reset Selection
                      </span>
                    </h2>
                    <PersistentTable
                      key={`${fundsTableId}_${count}`}
                      tableId={fundsTableId}
                      columns={columns.filter(
                        (row: any) => row.accessorKey !== 'allocation'
                      )}
                      height={'calc(100vh - 250px)'}
                      data={investments}
                      fetchData={fetchInvestments}
                      totalRows={totalRows}
                      onRowSelectionChange={setSelectedRows}
                      enableBottomToolbar
                      enableRowSelection
                      enablePagination
                      enableColumnFilters={false}
                      initialColumnVisibility={initialColumnVisibility}
                      dynamicColumns={dynamicColumns}
                      rowClick={handleRowClick}
                      setParentSorting={setSorting}
                      setGlobalFilter={setGlobalFilter}
                      manualFiltering
                      manualSorting
                      isLoading={isTableLoading}
                      defaultPageSize={100}
                      enableRowVirtualization
                      renderTopToolbarCustomActions={() => {
                        const filterDataForExport = (data: any) => {
                          return (Array.isArray(data) ? data : []).map(
                            (item: any) => {
                              return investmentColumns.reduce(
                                (acc: any, column: any) => {
                                  if (
                                    column.accessorKey &&
                                    !column.exportIgnore
                                  ) {
                                    // Ignore flagged columns
                                    let value: {} | null | undefined;

                                    if (column.exportAccessorFn) {
                                      value = column.exportAccessorFn(item);
                                    } else {
                                      value =
                                        column.accessorFn && item
                                          ? column.accessorFn(item)
                                          : item?.[column.accessorKey] ?? '';

                                      if (column.Cell) {
                                        const fakeCell = {
                                          getValue: () => value
                                        };
                                        const fakeRow = { original: item };
                                        value = column.Cell({
                                          cell: fakeCell,
                                          row: fakeRow
                                        });

                                        // Convert JSX to plain text (avoid [object Object] issues)
                                        if (React.isValidElement(value)) {
                                          value =
                                            item[column.accessorKey] ?? '-'; // Use raw data if JSX
                                        }
                                      }
                                    }

                                    acc[column.header] = value;
                                  }
                                  return acc;
                                },
                                {}
                              );
                            }
                          );
                        };

                        return (
                          <div
                            style={{
                              display: 'flex',
                              padding: '8px',
                              flexWrap: 'wrap',
                              gap: '10px'
                            }}
                          >
                            {/* <Button
                              variant="contained"
                              color="secondary"
                              size="small"
                              startIcon={<FileDownloadIcon />}
                            >
                              <CSVDownloader
                                filename={'Exported Portfolio Data'}
                                bom={true}
                                data={filterDataForExport(investments)}
                              >
                                Export All Data
                              </CSVDownloader>
                            </Button> */}
                            <Button
                              variant="contained"
                              color="secondary"
                              size="small"
                              startIcon={<FileDownloadIcon />}
                              disabled={selectedRows.length === 0}
                            >
                              <CSVDownloader
                                filename={`${type} filter research (${moment().format('DD-MM-YYYY')})`}
                                bom={true}
                                data={filterDataForExport(selectedRows)}
                              >
                                Export Selected Rows
                              </CSVDownloader>
                            </Button>
                            <Button
                              variant="contained"
                              color="secondary"
                              disabled={!selectedRows.length}
                              onClick={() => {
                                addInvestments(selectedRows);
                              }}
                            >
                              Add to Inv Selection
                            </Button>
                          </div>
                        );
                      }}
                    />
                  </div>
                </Grid>
              </Grid>
            </div>
          </LocalizationProvider>
        )}
        <ExistingAdvancedFiltersModal
          isModalOpen={showExistingModal}
          onClose={() => setShowExistingModal(false)}
          setFilters={(filter: any) => {
            setFilters(filter.filters);
          }}
        />
        <SaveAdvancedFilterModal
          initialValues={{
            name: ''
          }}
          onSubmit={(values: any) =>
            api
              .saveAdvancedFilter({ name: values.name, filters: filters })
              .then(({ data }) => {
                setFilters(data.filters);
                setShowSaveModal(false);
                Notify.success(`You have successfully saved your filters.`);
              })
          }
          isModalOpen={showSaveModal}
          onClose={() => setShowSaveModal(false)}
        />
        {type === 'portfolio' && (
          <PortfolioAllocationsModal
            isOpen={modalOpen}
            isLoading={fetchingPortfolio}
            setIsOpen={setModalOpen}
            columns={fundColumns}
            modelPortfolioName={selectedMpName}
            funds={funds}
          />
        )}
      </div>
      <Prompt
        isOpen={isModalVisible}
        title={`Are you sure you want to leave? Unsaved analysis will be lost.`}
        confirmAction={() => setConfirmLeave(true)}
        cancelAction={() => {
          setTargetLocation(null);
          setModalVisible(false);
        }}
        isProcessing={false}
      />
    </>
  ) : (
    <></>
  );
};

export default compose(
  connect(
    (state: any) => ({
      riskProfiler: selectors.firmSelectors.riskProfiler(state)
    }),
    null
  )
)(AdvancedFilters);
